import React, { useEffect, useState } from 'react';

import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { Modal } from '@/components/Modal';
import { Risk } from '@/types';
import { cn } from '@/utils/classname';

interface HackerOneSubmitModalProps {
  open: boolean;
  onClose: () => void;
  vulnerabilities: Risk[];
  onSubmit: (params: HackerOneSubmitParams) => Promise<void>;
  programUrl?: string;
}

export interface HackerOneSubmitParams {
  teamHandle: string;
  title?: string;
  severityRating?: string;
  vulnerabilityInformation?: string;
}

const HACKERONE_REPORT_TEMPLATE = `## Summary\n\n\n## Steps to Reproduce / Proof of Concept\n\n\n## Remediation Steps\n\n\n## Supporting Materials / References\n\n\n## Impact\n\n`;

export const HackerOneSubmitModal: React.FC<HackerOneSubmitModalProps> = ({
  open,
  onClose,
  vulnerabilities,
  onSubmit,
  programUrl,
}) => {
  const [teamHandle, setTeamHandle] = useState('');
  const [title, setTitle] = useState('');
  const [severityRating, setSeverityRating] = useState('');
  const [vulnInfo, setVulnInfo] = useState(HACKERONE_REPORT_TEMPLATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (programUrl && !teamHandle) {
      try {
        const url = new URL(programUrl);
        const segments = url.pathname.split('/').filter(Boolean);
        setTeamHandle(segments[segments.length - 1] || '');
      } catch {
        setTeamHandle(programUrl);
      }
    }
  }, [programUrl]);

  const handleSubmit = async () => {
    if (!teamHandle.trim()) {
      setError('HackerOne program handle is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await onSubmit({
        teamHandle: teamHandle.trim(),
        title: title.trim() || undefined,
        severityRating: severityRating || undefined,
        vulnerabilityInformation: vulnInfo.trim() || undefined,
      });
      setSuccess(
        `Submitted vulnerability to HackerOne program "${teamHandle}".`
      );
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { message?: string };
      setError(axiosErr?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTeamHandle('');
      setTitle('');
      setSeverityRating('');
      setVulnInfo('');
      setError('');
      setSuccess('');
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Modal
      size="xl"
      title="Submit to HackerOne"
      subtitle="Submit this vulnerability to a HackerOne bug bounty program"
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isSubmitting ? 'Submitting...' : 'Submit Report',
        onClick: handleSubmit,
        disabled: !teamHandle.trim() || isSubmitting || !!success,
      }}
    >
      <div className="space-y-4">
        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        <div>
          <label
            htmlFor="h1-team-handle"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Program Handle{' '}
            <span className="text-chariot-status-critical">*</span>
          </label>
          <input
            id="h1-team-handle"
            type="text"
            value={teamHandle}
            onChange={e => {
              setTeamHandle(e.target.value);
              if (error) setError('');
            }}
            placeholder="e.g. acme-corp (from hackerone.com/acme-corp)"
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
          <p className="text-chariot-text-secondary mt-1 text-xs">
            The program handle from the HackerOne URL, e.g. for
            hackerone.com/acme-corp enter acme-corp
          </p>
        </div>

        <div>
          <label
            htmlFor="h1-title"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Title Override
          </label>
          <input
            id="h1-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Leave blank to auto-populate from finding"
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />
        </div>

        <div>
          <label className="text-chariot-text-primary mb-1 block text-sm font-medium">
            Vulnerability Information
          </label>
          <div className="border-chariot-stroke bg-chariot-background-primary h-[300px] rounded-lg border">
            <MarkdownEditor
              value={vulnInfo}
              onChange={value => setVulnInfo(value || '')}
              preview="edit"
              className="h-full"
            />
          </div>
          <p className="text-chariot-text-secondary mt-1 text-xs">
            Detailed description of the vulnerability for the HackerOne report
          </p>
        </div>

        <div>
          <label
            htmlFor="h1-severity"
            className="text-chariot-text-primary mb-1 block text-sm font-medium"
          >
            Severity Override
          </label>
          <select
            id="h1-severity"
            value={severityRating}
            onChange={e => setSeverityRating(e.target.value)}
            disabled={isSubmitting || !!success}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
              'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            <option value="">Auto-detect from finding</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="none">None</option>
          </select>
        </div>

        {vulnerabilities.length > 0 && (
          <div className="border-chariot-stroke rounded-md border p-3">
            <p className="text-chariot-text-secondary mb-2 text-xs font-medium uppercase">
              Vulnerabilities to Submit
            </p>
            <ul className="space-y-1">
              {vulnerabilities.slice(0, 5).map(v => (
                <li key={v.key} className="text-chariot-text-primary text-sm">
                  {v.name}
                </li>
              ))}
              {vulnerabilities.length > 5 && (
                <li className="text-chariot-text-secondary text-sm">
                  ...and {vulnerabilities.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
